import React, { useEffect, useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import axios, { AxiosError } from "axios";
import { clearSessionStorage, getNewToken, postRequest } from "../../services/httpservice"; // adjust path if needed
import Loader from "../../components/Loader";
import { apiURL } from "../../constants/apiUrlConstants";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

type ApiResponse = {
  civilid: string;
  xpay: {
    xpayEligibility: string;
    xpayCurrent: string;
  };
  spay: {
    spayEligibility: string;
    spayCurrent: string;
  };
  status: string;
};

const CreditLimits = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [selectedTab, setSelectedTab] = useState<"xpay" | "spay">("xpay");
  const [loading, setLoading] = useState(false);

  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [toastCloseInMs, setToastCloseInMs] = useState<number>(0);
  const civilId = sessionStorage.getItem("civilId");
  const navigate = useNavigate();
  useEffect(() => {
    if (civilId) {
      const msToast = process.env.REACT_APP_TOAST_CLOSE_IN_SECONDS;
      if (msToast) {
        setToastCloseInMs(parseInt(msToast));
      }
      fetchLimits();
    }
  }, [civilId]);
  const fetchLimits = async () => {
    if (!civilId) return;

    try {
      setLoading(true);

      const accessToken = sessionStorage.getItem("accessToken");

      if (!accessToken) {
        navigate("/");
        return;
      }

      const response = await postRequest(
        process.env.REACT_APP_API_URL + apiURL.GET_CREDIT_LIMITS,
        {
          civilId: civilId,
        }
      );

      if (response == null) {
        toast.dismiss();
        toast.error(process.env.REACT_APP_ERROR_MSG);
        return;
      }

      if (response.status === 401) {
        toast.error("Session expired");
        clearSessionStorage();

        setTimeout(() => {
          navigate("/");
        }, 2000);

        return;
      }

      if (response.data === "") {
        toast.dismiss();
        toast.error(process.env.REACT_APP_ERROR_MSG);
        return;
      }

      setApiData(response.data);


      getNewToken();

    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        if (axiosError.response?.status === 401) {
          toast.error("Session expired");
          clearSessionStorage();

          setTimeout(() => {
            navigate("/");
          }, 2000);
        } else {
          toast.error(process.env.REACT_APP_ERROR_MSG);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchLimits();
  }, [civilId]);

  const formatAvailable = (value: number) => {
    const fixed = value.toFixed(3);
    return fixed.endsWith(".000") ? fixed.slice(0, -4) : fixed;
  };

  const current = apiData
    ? selectedTab === "xpay"
      ? {
        limit: apiData.xpay.xpayEligibility,
        used: apiData.xpay.xpayCurrent,
        available: formatAvailable(
          Number(apiData.xpay.xpayEligibility) -
          Number(apiData.xpay.xpayCurrent)
        ),
      }
      : {
        limit: apiData.spay.spayEligibility,
        used: apiData.spay.spayCurrent,
        available: formatAvailable(
          Number(apiData.spay.spayEligibility) -
          Number(apiData.spay.spayCurrent)
        ),
      }
    : { limit: "0", used: "0", available: "0" };

  const cardStyle = {
    flex: 1,
    color: "#000000",
    border: "1px solid #2493C7",
    borderRadius: "8px",
    background: "#EAF4F8",
    height: "110px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  };
  const tabButtonStyle = (active: boolean) => ({
    width: "110px",
    borderRadius: "4px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 400,
    padding: "6px 16px",
    boxShadow: "none",
    backgroundColor: active ? "#2493C7" : "#FFFFFF",
    color: active ? "#FFFFFF" : "#000000",
    border: "1px solid #D9D9D9",

    "&:hover": {
      backgroundColor: active ? "#2493C7" : "#FFFFFF",
      boxShadow: "none",
    },
  });
  return (
    <Box sx={{ width: "100%", overflow: "hidden", }}>
      {loading && <Loader />}

      {/* Tabs */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          px: 2,
          py: 0,
          pb: 2,
          // borderBottom: "1px solid #E5E5E5",
          flexWrap: "wrap",
          fontWeight: "400"
        }}
      >
        <Button
          variant={selectedTab === "xpay" ? "contained" : "outlined"}
          onClick={() => setSelectedTab("xpay")}
          sx={tabButtonStyle(selectedTab === "xpay")}
        >
          XPay
        </Button>

        {/* <Button
          variant={selectedTab === "spay" ? "contained" : "outlined"}
          onClick={() => setSelectedTab("spay")}
          disabled
          sx={tabButtonStyle(selectedTab === "spay")}
        >
          Safat Pay
        </Button> */}
      </Box>
      <Box
        sx={{
          height: "1px",
          backgroundColor: "#E5E5E5",
          width: "100vw",          // full viewport width
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",     // break container padding
          marginRight: "-50vw",
        }}
      />
      {/* Cards */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          p: 3,
          padding: "16px",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Box sx={cardStyle}>
          <Typography>
            {selectedTab === "xpay" ? t("profile.xpayLimit") : t("profile.spayLimit")}
          </Typography>
          <Typography fontFamily="Lexend"
            fontWeight={700}
            fontSize="16px"
          >{current.limit}   KWD</Typography>
        </Box>

        <Box sx={cardStyle}>
          <Typography>{t("profile.youveUsed")}</Typography>
          <Typography fontFamily="Lexend"
            fontWeight={700}
            fontSize="16px"
          >{current.used}  KWD</Typography>
        </Box>

        <Box sx={cardStyle}>
          <Typography>{t("profile.available")}</Typography>
          <Typography fontFamily="Lexend"
            fontWeight={700}
            fontSize="16px"
          >{current.available}  KWD</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CreditLimits;